import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

interface PublishReviewParams {
  reviewId: number;
  moderationNotes?: string;
}

export function usePublishReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['admin', 'publishReview'],
    mutationFn: async ({ reviewId, moderationNotes }: PublishReviewParams) => {
      return await api.admin.reviewsV2.publishReview({
        params: { review: reviewId },
        body: { moderation_notes: moderationNotes || '' },
      });
    },
    onMutate: async ({ reviewId }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'reviews', 'pending'] });

      const previousData = queryClient.getQueryData(['admin', 'reviews', 'pending']);

      queryClient.setQueryData(['admin', 'reviews', 'pending'], (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((review: any) => review.id !== reviewId),
            meta: page.meta
              ? {
                  ...page.meta,
                  total_pending: Math.max(0, (page.meta.total_pending || 0) - 1),
                }
              : page.meta,
          })),
        };
      });

      return { previousData };
    },
    onError: (error: any, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['admin', 'reviews', 'pending'], context.previousData);
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to publish review. Please try again.';

      toaster.create({
        title: 'Error publishing review',
        description: errorMessage,
        type: 'error',
      });

      console.error('[usePublishReview] Error:', error);
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review published successfully',
        description: 'The review is now visible to the public.',
        type: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'all'] });
    },
  });

  return {
    publishReview: mutation.mutate,
    publishReviewAsync: mutation.mutateAsync,
    isPublishing: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

